import { FunctionComponent, MouseEventHandler, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { trackStep } from '@/utils/adobeLaunch';
import { assertDefined } from '@/utils/assertDefined';
import { t } from '@/utils/i18n';
import { getParams, isAdminMode, isTemplateAdminMode, isUserAreaMode } from '@/utils/params';
import { getAppearance } from '@/model/draft/getAppearance';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getProductType } from '@/model/draft/getProductType';
import { getSelectedSizes } from '@/model/draft/getSelectedSizes';
import { getViewId } from '@/model/printArea/getViewId';
import { getSizesByAvailability } from '@/model/productType/getSizesByAvailability';
import { isOneSizeProductType } from '@/model/productType/isOneSizeProductType';
import { selectPriceBreakdown } from '@/redux/common/getters/selectPriceBreakdown';
import { getCurrencyId } from '@/redux/core/getters/getCurrencyId';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getDraft } from '@/redux/draft/getters/getDraft';
import { getProductType as getCurrentProductType } from '@/redux/draft/getters/getProductType';
import { isDraftValid } from '@/redux/draft/getters/isDraftValid';
import { saveDraftAction } from '@/redux/product/actions/saveDraftAction';
import { saveProductAction } from '@/redux/product/actions/saveProductAction';
import { StoreDispatch, StoreState } from '@/redux/types';
import { Button } from '@/components/common/Button';
import { Price } from '@/components/common/Price';
import { AlertModal } from '@/components/common/modal/modal';
import { setSelectedConfigurationAction } from '@/components/draft/draftState/actions/setSelectedConfigurationAction';
import { SaveDialog, useSaveDialog } from '../common/sidebar/SaveDialog';
import './CTA.scss';

export const CTA: FunctionComponent = () => {
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string>();
  const productValid = useSelector(isDraftValid);
  const basketItemId = useSelector((state: StoreState) => getDraft(state).basketItemId);
  const currencyId = useSelector(getCurrencyId);
  const productType = useSelector(getCurrentProductType);

  const store = useStore();
  const dispatch = useDispatch<StoreDispatch>();
  const draftDispatch = useReduxDraftDispatch();
  const [saveState, saveDispatch] = useSaveDialog();

  const { createOrUpdateProduct } = getParams();

  const getLabel = () => {
    if (isUserAreaMode()) {
      return t('basket.createProduct');
    }
    if (isAdminMode() || basketItemId) {
      return t('basket.updateProduct');
    }
    if (isTemplateAdminMode()) {
      return 'Save Template';
    }

    return isOneSizeProductType(productType)
      ? t('basket.selectSizesUnisize')
      : t('basket.selectSizesNonUnisize');
  };

  const handleClick: MouseEventHandler = () => {
    const state = store.getState() as StoreState;
    const draft = getDraft(state);
    const productType = getProductType(draft);
    const appearance = getAppearance(draft);
    const selectedSizes = getSelectedSizes(draft);
    const configurations = getConfigurations(draft);
    const priceBreakdown = selectPriceBreakdown(state);

    if (createOrUpdateProduct) {
      // handleProductCreation
      saveDispatch({ type: 'save-start', payload: configurations });

      return dispatch(saveProductAction(() => saveDispatch({ type: 'save-update' }))).then(
        (productReference) => {
          // find the first selected or available size
          const sizeId =
            Object.entries(selectedSizes).find(([, count]) => count > 0)?.[0] ||
            getSizesByAvailability(productType, appearance, true)[0]?.id;
          createOrUpdateProduct(
            {
              id: productReference.id,
              appearanceId: appearance.id,
              sizeId,
            },
            (e) => {
              if (e) {
                setError(t('error.defaultError'));
              } else {
                setIsSuccess(true);
              }
            }
          );
          saveDispatch({ type: 'save-success' });
        },
        (e) => {
          setError(t('error.defaultError'));
          saveDispatch({ type: 'save-error', payload: e });
        }
      );
    } else if (isTemplateAdminMode()) {
      return dispatch(saveDraftAction(draft, assertDefined(getParams().onTemplateSave)));
    }

    trackStep('getPrice', {
      products: [
        {
          productTypeId: productType.id,
          appearanceId: appearance.id,
          sizeId: null,
          price: priceBreakdown.product,
          quantity: null,
          availableSizeIds: getSizesByAvailability(productType, appearance, true).map((s) => s.id),
          unavailableSizeIds: getSizesByAvailability(productType, appearance, false).map(
            (s) => s.id
          ),
          configurations: configurations.map((c) => ({
            printAreaId: c.printArea.id,
            printTypeId: c.printType.id,
            type: c.type,
            viewId: getViewId(c.printArea),
          })),
        },
      ],
      currencyId: currencyId,
      volumeDiscount: null,
    });
    draftDispatch(setSelectedConfigurationAction());
    navigate('/price');
  };

  return (
    <div className="cta">
      {isUserAreaMode() && <UserAreaProductPrice />}
      {createOrUpdateProduct && <SaveDialog saveState={saveState} dispatch={dispatch} />}
      <Button onClick={handleClick} key="button" disabled={!productValid || saveState.loading}>
        {getLabel()}
      </Button>
      {isSuccess && (
        <AlertModal
          title={t('errorDialog.productHeadline')}
          message={t('errorDialog.createProduct')}
          onAccept={() => setIsSuccess(false)}
        >
          <p>{t('errorDialog.productSuccess')}</p>
        </AlertModal>
      )}
      {error && (
        <AlertModal
          title={t('errorDialog.title')}
          message={t('errorDialog.button')}
          onAccept={() => setError(undefined)}
        >
          <p>{error}</p>
        </AlertModal>
      )}
    </div>
  );
};

const UserAreaProductPrice: FunctionComponent = () => {
  const priceBreakdown = useSelector(selectPriceBreakdown);
  return <Price value={priceBreakdown.product} className="cta__product-price" />;
};
